import { useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTracking } from './useTracking';

// Key to store product data on elements
const PRODUCT_DATA_KEY = '__visibilityTrackingProduct';

/**
 * Hook for tracking products as they become visible during scroll
 * Sends view_item_list events in batches as products enter the viewport
 *
 * @param {Object} options
 * @param {string} options.listName - Name of the product list
 * @param {string} options.listId - ID of the product list
 * @param {number} options.debounceMs - Debounce time in ms (default: 500)
 * @param {number} options.threshold - IntersectionObserver threshold (default: 0.5)
 * @returns {Object} { trackProductVisibility, resetTracking }
 */
export function useProductVisibilityTracking({
  listName,
  listId,
  debounceMs = 500,
  threshold = 0.5,
  routeParents = null // Route parents for proper category names
} = {}) {
  const { trackViewItemList } = useTracking();

  // Store values in refs to avoid recreating observer on every render
  const trackViewItemListRef = useRef(trackViewItemList);
  const listNameRef = useRef(listName);
  const listIdRef = useRef(listId);
  const debounceRef = useRef(debounceMs);
  const routeParentsRef = useRef(routeParents);

  // Keep refs updated
  useEffect(() => {
    trackViewItemListRef.current = trackViewItemList;
    listNameRef.current = listName;
    listIdRef.current = listId;
    debounceRef.current = debounceMs;
    routeParentsRef.current = routeParents;
  }, [trackViewItemList, listName, listId, debounceMs, routeParents]);

  // Track which products have been seen (by articleNumber)
  const seenProductsRef = useRef(new Set());
  // Products waiting to be tracked (accumulated during debounce)
  const pendingProductsRef = useRef([]);
  // Debounce timer
  const debounceTimerRef = useRef(null);
  // Observer instance
  const observerRef = useRef(null);
  // Queue for elements registered before observer is ready
  const pendingObserveQueueRef = useRef([]);

  // Send tracking event for pending products
  const flushPendingProducts = useCallback(() => {
    if (pendingProductsRef.current.length === 0) return;

    const productsToTrack = [...pendingProductsRef.current];
    pendingProductsRef.current = [];

    trackViewItemListRef.current({
      products: productsToTrack,
      listName: listNameRef.current || 'Product Grid',
      listId: listIdRef.current,
      routeParents: routeParentsRef.current
    });
  }, []);

  // Schedule tracking with debounce
  const scheduleTracking = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      flushPendingProducts();
    }, debounceRef.current);
  }, [flushPendingProducts]);

  // Handle product becoming visible
  const handleProductVisible = useCallback(
    product => {
      const productKey = product?.articleNumber || product?.id || product?.key;
      if (!productKey) return;

      // Skip if already seen
      if (seenProductsRef.current.has(productKey)) return;

      // Mark as seen
      seenProductsRef.current.add(productKey);

      // Add to pending list
      const productWithIndex = {
        ...product,
        _trackingIndex: seenProductsRef.current.size - 1
      };
      pendingProductsRef.current.push(productWithIndex);

      // Schedule batch tracking
      scheduleTracking();
    },
    [scheduleTracking]
  );

  // Store handleProductVisible in ref
  const handleProductVisibleRef = useRef(handleProductVisible);
  useEffect(() => {
    handleProductVisibleRef.current = handleProductVisible;
  }, [handleProductVisible]);

  // Create the IntersectionObserver
  useLayoutEffect(() => {
    if (
      typeof IntersectionObserver === 'undefined' ||
      typeof window === 'undefined'
    ) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          // Get product data stored directly on the element
          const product = entry.target[PRODUCT_DATA_KEY];

          if (entry.isIntersecting && product) {
            handleProductVisibleRef.current(product);
            // Stop observing this element
            observerRef.current?.unobserve(entry.target);
            // Clean up the stored data
            delete entry.target[PRODUCT_DATA_KEY];
          }
        });
      },
      { threshold }
    );

    // Process any queued elements
    if (pendingObserveQueueRef.current.length > 0) {
      pendingObserveQueueRef.current.forEach(({ element, product }) => {
        const productKey =
          product?.articleNumber || product?.id || product?.key;
        if (!seenProductsRef.current.has(productKey) && element) {
          // Store product data directly on the element
          element[PRODUCT_DATA_KEY] = product;
          observerRef.current.observe(element);
        }
      });
      pendingObserveQueueRef.current = [];
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        flushPendingProducts();
      }
    };
  }, [threshold, flushPendingProducts]);

  // Function to start tracking a product element
  const trackProductVisibility = useCallback((element, product) => {
    if (!element || !product) return;

    const productKey = product?.articleNumber || product?.id || product?.key;

    // Skip if already seen
    if (seenProductsRef.current.has(productKey)) return;

    // Store product data directly on the element
    element[PRODUCT_DATA_KEY] = product;

    // If observer isn't ready yet, queue the element
    if (!observerRef.current) {
      pendingObserveQueueRef.current.push({ element, product });

      return () => {
        delete element[PRODUCT_DATA_KEY];
        pendingObserveQueueRef.current = pendingObserveQueueRef.current.filter(
          item => item.element !== element
        );
      };
    }

    // Observe the element
    observerRef.current.observe(element);

    // Return cleanup function
    return () => {
      delete element[PRODUCT_DATA_KEY];
      if (observerRef.current && element) {
        observerRef.current.unobserve(element);
      }
    };
  }, []);

  // Reset tracking
  const resetTracking = useCallback(() => {
    seenProductsRef.current.clear();
    pendingProductsRef.current = [];
    pendingObserveQueueRef.current = [];
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, []);

  return {
    trackProductVisibility,
    resetTracking
  };
}
